/**
 * Sprint Funnel · #3 — Tab "Funnel" del SuperAdmin.
 *
 * Etapas:
 *  Marketplace views → Profile views → Leads → Claim started → Claim completed
 *    → Trial started → Subscription started
 *
 * Muestra:
 *  · Conteo por etapa
 *  · % vs etapa anterior (conversion)
 *  · Drop-off por etapa
 *  · % vs primera etapa (overall conversion)
 *  · Top orgs por views/leads/claims/trials
 *  · Mini-timeseries de últimos 30 días
 */
import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Funnel, ArrowDown, TrendDown, Trophy, ArrowsClockwise } from "@phosphor-icons/react";

const PERIODS = [
  { id: 7, label: "7 días" },
  { id: 30, label: "30 días" },
  { id: 90, label: "90 días" },
  { id: 0, label: "Todo" },
];

const TOP_METRICS = [
  { id: "views", label: "Vistas" },
  { id: "leads", label: "Leads" },
  { id: "claims", label: "Reclamaciones" },
  { id: "trials", label: "Trials" },
];

export default function FunnelTab() {
  const [days, setDays] = useState(30);
  const [topMetric, setTopMetric] = useState("views");
  const [funnel, setFunnel] = useState(null);
  const [topOrgs, setTopOrgs] = useState([]);
  const [series, setSeries] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    setLoading(true);
    try {
      const tsDays = days <= 0 ? 30 : Math.min(days, 90);
      const [funnelRes, topRes, tsRes] = await Promise.all([
        api.get(`/superadmin/funnel?days=${days}`),
        api.get(`/superadmin/top-orgs?metric=${topMetric}&days=${days}&limit=10`),
        api.get(`/superadmin/funnel/timeseries?days=${tsDays}`),
      ]);
      setFunnel(funnelRes.data);
      setTopOrgs(topRes.data?.items || []);
      setSeries(tsRes.data?.series || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    refresh(); /* eslint-disable-line react-hooks/exhaustive-deps */
  }, [days, topMetric]);

  return (
    <div className="space-y-6" data-testid="superadmin-funnel-tab">
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <p className="text-[11px] uppercase tracking-[0.2em] text-[var(--ag-gold)] font-semibold">Validación del modelo</p>
          <h2 className="text-xl md:text-2xl font-bold tracking-tight mt-1 flex items-center gap-2">
            <Funnel size={20} weight="fill" /> Funnel Visita → Pago
          </h2>
          <p className="text-xs text-[var(--ag-text-dim)] mt-1">
            Mide la conversión real desde la primera visita al marketplace hasta una suscripción de pago.
          </p>
        </div>
        <button type="button" onClick={refresh} className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-[var(--ag-text-dim)] hover:text-white border border-[var(--ag-border)] rounded transition-colors" data-testid="funnel-refresh-btn">
          <ArrowsClockwise size={14} /> Refrescar
        </button>
      </header>

      {/* Period tabs */}
      <div className="flex flex-wrap gap-2" data-testid="funnel-period-tabs">
        {PERIODS.map((p) => (
          <button key={p.id} type="button" onClick={() => setDays(p.id)} data-testid={`funnel-period-${p.id}`}
            className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors ${
              days === p.id ? "bg-[var(--ag-gold)] text-[var(--ag-midnight)] border-[var(--ag-gold)]" : "border-[var(--ag-border)] text-[var(--ag-text-dim)] hover:text-white"
            }`}>
            {p.label}
          </button>
        ))}
      </div>

      {loading || !funnel ? (
        <div className="p-10 text-center text-[var(--ag-text-dim)] text-sm" data-testid="funnel-loading">Cargando…</div>
      ) : (
        <>
          {/* Funnel bars */}
          <div className="space-y-1.5" data-testid="funnel-stages">
            {funnel.stages.map((s, i) => (
              <FunnelStage key={s.id} stage={s} maxCount={funnel.stages[0]?.count || 1} index={i} />
            ))}
          </div>

          {/* Mini timeseries */}
          <div className="border border-[var(--ag-border)] rounded-lg p-4 bg-[var(--ag-midnight-soft)]/40" data-testid="funnel-timeseries-card">
            <h3 className="text-sm font-semibold mb-3">Evolución (últimos {Math.min(days || 30, 90)} días)</h3>
            <DualLineChart series={series} />
          </div>

          {/* Top orgs */}
          <div className="border border-[var(--ag-border)] rounded-lg p-4 bg-[var(--ag-midnight-soft)]/40" data-testid="funnel-top-orgs">
            <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
              <h3 className="text-sm font-semibold flex items-center gap-2"><Trophy size={16} weight="fill" className="text-[var(--ag-gold)]" /> Top 10 perfiles</h3>
              <div className="flex flex-wrap gap-1.5">
                {TOP_METRICS.map((m) => (
                  <button key={m.id} type="button" onClick={() => setTopMetric(m.id)} data-testid={`top-metric-${m.id}`}
                    className={`px-2 py-1 text-[11px] font-medium rounded border transition-colors ${
                      topMetric === m.id ? "bg-[var(--ag-gold)] text-[var(--ag-midnight)] border-[var(--ag-gold)]" : "border-[var(--ag-border)] text-[var(--ag-text-dim)] hover:text-white"
                    }`}>{m.label}</button>
                ))}
              </div>
            </div>
            {topOrgs.length === 0 ? (
              <p className="text-xs text-[var(--ag-text-dim)] italic py-4 text-center">Sin datos en este periodo.</p>
            ) : (
              <table className="w-full text-sm" data-testid="top-orgs-table">
                <thead>
                  <tr className="text-[10px] uppercase tracking-wider text-[var(--ag-text-dim)] border-b border-[var(--ag-border)]">
                    <th className="text-left py-2">#</th>
                    <th className="text-left py-2">Negocio</th>
                    <th className="text-left py-2 hidden sm:table-cell">Ciudad</th>
                    <th className="text-right py-2 tabular-nums">{TOP_METRICS.find((m) => m.id === topMetric)?.label}</th>
                  </tr>
                </thead>
                <tbody>
                  {topOrgs.map((o, i) => (
                    <tr key={o.org_id || i} className="border-b border-[var(--ag-border)]/50 last:border-0">
                      <td className="py-2 text-xs text-[var(--ag-text-dim)] tabular-nums">{i + 1}</td>
                      <td className="py-2">
                        <a href={`/centro/${o.slug}`} target="_blank" rel="noopener noreferrer" className="hover:underline">{o.name}</a>
                        <div className="text-[10px] text-[var(--ag-text-dim)] font-mono truncate max-w-[260px]">/{o.slug}</div>
                      </td>
                      <td className="py-2 text-xs text-[var(--ag-text-dim)] hidden sm:table-cell">{o.city || "—"}</td>
                      <td className="py-2 text-right font-bold tabular-nums">{o.value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function FunnelStage({ stage, maxCount, index }) {
  const widthPct = maxCount ? Math.max(2, (stage.count / maxCount) * 100) : 2;
  const isFirst = index === 0;
  return (
    <div className="border border-[var(--ag-border)] rounded-lg p-3 bg-[var(--ag-midnight-soft)]/30" data-testid={`funnel-stage-${stage.id}`}>
      <div className="flex items-center justify-between gap-3 mb-1.5">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-[10px] tabular-nums text-[var(--ag-text-dim)] font-bold w-5">{index + 1}.</span>
          <span className="font-semibold text-sm truncate">{stage.label}</span>
        </div>
        <div className="flex items-center gap-3 text-xs flex-shrink-0">
          <span className="font-bold text-lg tabular-nums text-[var(--ag-text)]" data-testid={`funnel-stage-${stage.id}-count`}>{stage.count}</span>
          {!isFirst && (
            <>
              <span className="text-[10px] uppercase text-[var(--ag-text-dim)]" title="Conversión desde la etapa anterior">
                {stage.from_prev_pct}%
              </span>
              {stage.dropoff_pct > 0 && (
                <span className="inline-flex items-center gap-0.5 text-[10px] text-red-300/80" title="Drop-off vs etapa anterior">
                  <TrendDown size={10} /> {stage.dropoff_pct}%
                </span>
              )}
            </>
          )}
        </div>
      </div>
      <div className="h-2 w-full bg-[var(--ag-midnight)] border border-[var(--ag-border)]/50 rounded-full overflow-hidden">
        <div className="h-full bg-gradient-to-r from-[var(--ag-gold)] to-[var(--ag-gold-strong)]" style={{ width: `${widthPct}%` }} data-testid={`funnel-stage-${stage.id}-bar`} />
      </div>
      <div className="mt-1 flex items-center justify-between text-[10px] text-[var(--ag-text-dim)] tabular-nums">
        <span>vs. inicio: {stage.from_first_pct}%</span>
        {!isFirst && <span className="inline-flex items-center gap-0.5"><ArrowDown size={10} /> de {stage.from_prev_pct}% del paso anterior</span>}
      </div>
    </div>
  );
}

function DualLineChart({ series }) {
  if (!series || series.length === 0) {
    return <div className="h-32 grid place-items-center text-xs text-[var(--ag-text-dim)] italic">Sin datos aún.</div>;
  }
  const w = 720, h = 140;
  const padL = 30, padR = 8, padT = 12, padB = 18;
  const innerW = w - padL - padR;
  const innerH = h - padT - padB;
  const max = Math.max(1, ...series.map((p) => Math.max(p.views, p.leads, p.claims)));
  const stepX = innerW / Math.max(1, series.length - 1);
  const linePath = (key) => series.map((p, i) => {
    const x = padL + i * stepX;
    const y = padT + innerH - (p[key] / max) * innerH;
    return `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(" ");

  return (
    <div data-testid="funnel-dual-chart">
      <div className="flex items-center gap-4 text-[11px] mb-2">
        <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-[#FFD700]" /> Vistas</span>
        <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-[#F472B6]" /> Leads</span>
        <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-[#7DD3FC]" /> Reclamaciones</span>
      </div>
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-32">
        {[0, 0.5, 1].map((t) => (
          <line key={t} x1={padL} x2={padL + innerW} y1={padT + innerH * t} y2={padT + innerH * t} stroke="rgba(255,255,255,0.06)" strokeDasharray="3,3" />
        ))}
        <path d={linePath("views")} fill="none" stroke="#FFD700" strokeWidth="1.8" strokeLinejoin="round" />
        <path d={linePath("leads")} fill="none" stroke="#F472B6" strokeWidth="1.8" strokeLinejoin="round" />
        <path d={linePath("claims")} fill="none" stroke="#7DD3FC" strokeWidth="1.8" strokeLinejoin="round" />
        <text x={4} y={padT + 4} fontSize="9" fill="rgba(255,255,255,0.5)">{max}</text>
        <text x={4} y={padT + innerH + 3} fontSize="9" fill="rgba(255,255,255,0.5)">0</text>
      </svg>
    </div>
  );
}
